import { AttributesSelector, Box, Checkbox, Text } from '@workwhile/ui'
import React, { useMemo } from 'react'
import { FormProvider, useFormContext, Path, PathValue } from 'react-hook-form'

export interface SelectOption<T> {
  label: string
  value: T
}

export interface SelectFilterProps<
  FilterSchema extends Record<string, any>,
  T,
> {
  fieldPath: Path<FilterSchema>
  label: string
  filterKey: string
  options: SelectOption<T>[]
  inputLabel?: string
  formatSelectedAttributes?: (values: T[]) => string[]
  children?: React.ReactNode
}

export const SelectFilter = <
  FilterSchema extends Record<string, any>,
  T extends string | number,
>({
  fieldPath,
  label,
  filterKey,
  options,
  inputLabel,
  formatSelectedAttributes,
  children,
}: SelectFilterProps<FilterSchema, T>) => {
  const methods = useFormContext<FilterSchema>()

  const selectedValues = (methods.watch(fieldPath) as T[]) || []

  const resetToDefaultValues = () => {
    methods.setValue(
      fieldPath,
      [] as PathValue<FilterSchema, Path<FilterSchema>>
    )
  }

  const selectedAttributes = useMemo(() => {
    if (formatSelectedAttributes) {
      return formatSelectedAttributes(selectedValues)
    }
    return selectedValues.map((value) => String(value))
  }, [selectedValues, formatSelectedAttributes])

  const handleValueChange = (value: T, checked: boolean) => {
    const currentValues = selectedValues
    if (checked) {
      methods.setValue(fieldPath, [...currentValues, value] as PathValue<
        FilterSchema,
        Path<FilterSchema>
      >)
    } else {
      methods.setValue(
        fieldPath,
        currentValues.filter((v) => v !== value) as PathValue<
          FilterSchema,
          Path<FilterSchema>
        >
      )
    }
  }

  const inputComponent = children || (
    <Box>
      {inputLabel ? (
        <Text fontSize="sm" fontWeight="medium" mb={2}>
          {inputLabel}
        </Text>
      ) : null}
      {options.map((option) => (
        <Checkbox
          key={String(option.value)}
          value={selectedValues.includes(option.value)}
          onChange={(checked) => handleValueChange(option.value, checked)}
        >
          {option.label}
        </Checkbox>
      ))}
    </Box>
  )

  return (
    <FormProvider {...methods}>
      <AttributesSelector
        label={label}
        rootGroupTitle="Filter by"
        selectedAttributes={selectedAttributes}
        onClearAll={resetToDefaultValues}
        groups={
          <AttributesSelector.Group
            key={filterKey}
            title={label}
            numberOfSelectedAttributes={selectedAttributes.length}
          >
            {inputComponent}
          </AttributesSelector.Group>
        }
      />
    </FormProvider>
  )
}
